import React from 'react';
import { useT } from '../i18n';
import testimonialShots from '../data/testimonials.json';
import proofVideos from '../data/proofVideos.json';

// Social proof for the pay-first decision points.
//  • Decision sheet (default): the customer-conversation video standalone,
//    sized to leave room for the pay/continue buttons right under it.
//  • Checkout form (showShots): one horizontal gallery — the video as the
//    FIRST slide, styled like the WhatsApp screenshot cards that follow it,
//    sized so 2–3 cards fill a phone screen.
// Both end with the Instagram provenance line: the handle is written out (and
// tappable) so a hesitant buyer can look us up on their own phone.
// The video is the first entry of data/proofVideos.json (a compressed 720p
// H.264 with faststart, hosted on GCS) — the /se page shows all of them.
const { src: PROOF_VIDEO, poster: PROOF_POSTER } = proofVideos[0];

// React doesn't always write the muted ATTRIBUTE to the DOM (facebook/
// react#10389); without it browsers block the autoplay. Set it imperatively,
// then nudge play() for browsers that already evaluated the autoplay policy
// before muted landed. The catch is fine — the visitor still has controls.
function initVideo(el) {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  if (el.paused) el.play().catch(() => {});
}

function ProofVideo({ className }) {
  return (
    <video
      className={className}
      src={PROOF_VIDEO}
      poster={PROOF_POSTER}
      ref={initVideo}
      autoPlay
      muted
      loop
      playsInline
      controls
      preload="metadata"
    />
  );
}

export function IgLine({ t }) {
  return (
    <a
      className="pf-proof-ig"
      href="https://instagram.com/anabatal.ai"
      target="_blank"
      rel="noreferrer"
    >
      {/* Same inline Instagram glyph the header uses — this lucide version
          dropped its brand icons. */}
      <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
        <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.73 1.46 1.38 2.13a5.88 5.88 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44z"/>
      </svg>
      <span>
        {t.pfProofIgCaption} <strong dir="ltr">@anabatal.ai</strong>
      </span>
    </a>
  );
}

export default function PayFirstProof({ showShots = false }) {
  const t = useT();

  if (!showShots) {
    return (
      <div className="pf-proof">
        <ProofVideo className="pf-proof-video" />
        <IgLine t={t} />
      </div>
    );
  }

  return (
    <div className="pf-proof">
      <p className="pf-proof-shots-title">{t.waProofTitle}</p>
      <div className="pf-proof-shots">
        <div className="pf-proof-shot pf-proof-shot--video">
          <ProofVideo className="pf-proof-slide-video" />
        </div>
        {testimonialShots.map((shot) => (
          <a key={shot.src} href={shot.src} target="_blank" rel="noreferrer" className="pf-proof-shot">
            <img src={shot.src} alt={t.waProofAlt} loading="lazy" width={shot.w} height={shot.h} />
          </a>
        ))}
      </div>
      <IgLine t={t} />
    </div>
  );
}
